import type { LucideIcon } from 'lucide-react';
import { CopilotCommand, dispatchCopilotCommand } from './copilotBus';
import {
  Home,
  MessageSquare,
  Users,
  Link2,
  BookOpen,
  Bot,
  BarChart3,
  Settings,
  MessageCircle,
  Facebook,
  Instagram,
  ShoppingBag,
  Store,
  Upload,
  HelpCircle,
  RefreshCw,
  Zap,
  Clock,
  Download,
} from 'lucide-react';

/** What an action gets to drive the app when it runs. */
export interface CopilotCtx {
  navigate: (href: string) => void;
  /** Smooth-scroll to and pulse an element tagged data-copilot-target="<id>". */
  spotlight: (target: string) => void;
  toast: (message: string) => void;
  /** Push an assistant message into the panel thread. */
  say: (text: string, extra?: { actionIds?: string[]; suggestions?: string[] }) => void;
  /** Close the panel/palette so the user can see the spotlighted UI. */
  close: () => void;
}

export type CopilotGroup = 'Navigate' | 'Channels' | 'Knowledge' | 'Automations' | 'Leads';

export interface CopilotAction {
  id: string;
  label: string;
  hint: string;
  keywords: string[];
  group: CopilotGroup;
  icon: LucideIcon;
  run: (ctx: CopilotCtx) => void;
}

const goto = (href: string) => (c: CopilotCtx) => {
  c.navigate(href);
  c.close();
};

/**
 * The single registry of everything the Copilot (and ⌘K palette) can do.
 * Add a capability here once and it shows up in both surfaces automatically.
 */
export const COPILOT_ACTIONS: CopilotAction[] = [
  // ── Navigate ────────────────────────────────────────────────
  { id: 'open-dashboard', label: 'Go to Dashboard', hint: 'Overview & live KPIs', keywords: ['dashboard', 'home', 'overview', 'kpi'], group: 'Navigate', icon: Home, run: goto('/dashboard') },
  { id: 'open-conversations', label: 'Open Conversations', hint: 'Unified customer inbox', keywords: ['conversations', 'inbox', 'chats', 'messages'], group: 'Navigate', icon: MessageSquare, run: goto('/conversations') },
  { id: 'open-leads', label: 'Open Leads', hint: 'Captured customer leads', keywords: ['leads', 'contacts', 'customers'], group: 'Navigate', icon: Users, run: goto('/leads') },
  { id: 'open-integrations', label: 'Open Integrations', hint: 'Channels & stores', keywords: ['integrations', 'connect', 'channels'], group: 'Navigate', icon: Link2, run: goto('/integrations') },
  { id: 'open-knowledge', label: 'Open Knowledge Base', hint: 'Products, prices & FAQs', keywords: ['knowledge', 'catalog', 'products', 'faq'], group: 'Navigate', icon: BookOpen, run: goto('/knowledge') },
  { id: 'open-automations', label: 'Open Automations', hint: 'AI reply rules & hours', keywords: ['automations', 'rules', 'auto reply', 'bot'], group: 'Navigate', icon: Bot, run: goto('/automations') },
  { id: 'open-analytics', label: 'Open Analytics', hint: 'Performance & trends', keywords: ['analytics', 'metrics', 'reports', 'stats'], group: 'Navigate', icon: BarChart3, run: goto('/analytics') },
  { id: 'open-settings', label: 'Open Settings', hint: 'Account, billing & team', keywords: ['settings', 'account', 'billing', 'profile'], group: 'Navigate', icon: Settings, run: goto('/settings') },

  // ── Channels ────────────────────────────────────────────────
  {
    id: 'connect-whatsapp', label: 'Connect WhatsApp Business', hint: 'Auto-reply on WhatsApp', keywords: ['whatsapp', 'wa', 'whats app'], group: 'Channels', icon: MessageCircle,
    run: (c) => { c.navigate('/integrations'); c.spotlight('integrations-whatsapp'); c.toast('Opening WhatsApp Business setup…'); c.close(); },
  },
  {
    id: 'connect-messenger', label: 'Connect Messenger', hint: 'Auto-reply to Facebook Page', keywords: ['messenger', 'facebook', 'fb', 'page'], group: 'Channels', icon: Facebook,
    run: (c) => { c.navigate('/integrations'); c.spotlight('integrations-facebook'); c.toast('Opening Messenger setup…'); c.close(); },
  },
  {
    id: 'connect-instagram', label: 'Connect Instagram', hint: 'Respond to DMs & comments', keywords: ['instagram', 'ig', 'insta', 'dm'], group: 'Channels', icon: Instagram,
    run: (c) => { c.navigate('/integrations'); c.spotlight('integrations-instagram'); c.toast('Opening Instagram setup…'); c.close(); },
  },
  {
    id: 'connect-shopify', label: 'Connect Shopify', hint: 'Sync products & orders', keywords: ['shopify', 'store', 'shop'], group: 'Channels', icon: ShoppingBag,
    run: (c) => { c.navigate('/integrations'); c.spotlight('integrations-shopify'); c.toast('Opening Shopify connection…'); c.close(); },
  },
  {
    id: 'connect-woocommerce', label: 'Connect WooCommerce', hint: 'Sync WordPress store', keywords: ['woocommerce', 'woo', 'wordpress'], group: 'Channels', icon: Store,
    run: (c) => { c.navigate('/integrations'); c.spotlight('integrations-woocommerce'); c.toast('Opening WooCommerce connection…'); c.close(); },
  },

  // ── Knowledge ───────────────────────────────────────────────
  {
    id: 'import-products-csv', label: 'Import products from CSV', hint: 'Bulk-add to knowledge base', keywords: ['import', 'csv', 'upload', 'products', 'bulk'], group: 'Knowledge', icon: Upload,
    run: (c) => { c.navigate('/knowledge'); c.spotlight('knowledge-sources'); dispatchCopilotCommand(CopilotCommand.KnowledgeOpenModal, 'csv'); c.close(); },
  },
  {
    id: 'add-product-manual', label: 'Add a product manually', hint: 'Open the add-product form', keywords: ['add product', 'manual', 'new product', 'create product'], group: 'Knowledge', icon: Upload,
    run: (c) => { c.navigate('/knowledge'); c.spotlight('knowledge-sources'); dispatchCopilotCommand(CopilotCommand.KnowledgeOpenModal, 'add'); c.close(); },
  },
  {
    id: 'add-faq', label: 'Add an FAQ or policy', hint: 'Teach the AI an answer', keywords: ['faq', 'policy', 'question', 'answer', 'returns'], group: 'Knowledge', icon: HelpCircle,
    run: (c) => { c.navigate('/knowledge'); c.spotlight('knowledge-faq'); c.close(); },
  },
  {
    id: 'reindex-knowledge', label: 'Re-index knowledge base', hint: 'Refresh what the AI knows', keywords: ['reindex', 're-index', 'refresh', 'sync knowledge'], group: 'Knowledge', icon: RefreshCw,
    run: (c) => { c.navigate('/knowledge'); c.spotlight('knowledge-summary'); dispatchCopilotCommand(CopilotCommand.KnowledgeReindex); c.close(); },
  },

  // ── Automations ─────────────────────────────────────────────
  {
    id: 'create-automation', label: 'Create an automation rule', hint: 'Keyword & event triggers', keywords: ['automation', 'rule', 'trigger', 'keyword'], group: 'Automations', icon: Zap,
    run: (c) => { c.navigate('/automations'); c.spotlight('automations-rules'); dispatchCopilotCommand(CopilotCommand.AutomationsAddRule); c.close(); },
  },
  {
    id: 'set-business-hours', label: 'Set business hours', hint: 'When the AI takes over', keywords: ['business hours', 'hours', 'availability', 'off hours'], group: 'Automations', icon: Clock,
    run: (c) => { c.navigate('/automations'); c.spotlight('automations-hours'); c.close(); },
  },

  // ── Leads ───────────────────────────────────────────────────
  {
    id: 'export-leads', label: 'Export leads', hint: 'Download as CSV', keywords: ['export', 'download', 'leads', 'csv'], group: 'Leads', icon: Download,
    run: (c) => { c.navigate('/leads'); c.spotlight('leads-header'); dispatchCopilotCommand(CopilotCommand.LeadsExport); c.close(); },
  },
];

export function getAction(id: string): CopilotAction | undefined {
  return COPILOT_ACTIONS.find((a) => a.id === id);
}
